import { Modal, Button, Spinner } from "react-bootstrap";
import { useState, useEffect } from "react";
import AtributosModal from "./attributesModal";
import AttributesValueModal from "./attributesValueModal";
import { useToken } from "../../../hooks/tokenContext";
import AtributosTable from "./attributesTable";
import useApi from "../../../hooks/apiService";
import useResources from "../../../hooks/useResources";
import NavigationTabs from "../../../components/navigationTabs";
import { useTranslation } from "react-i18next";
import NoDataTables from "../../../components/noDataTables";
import ModalResourceAttributeValues from "../../../components/modalResourceAttributeValues";
import ItemAttributesModal from "../../../components/modalAllAttributeValues";

function Atributos({ disableLocking = false }) {
  const { t } = useTranslation();
  const { fetchData, deleteData, updateData, postData } = useApi();
  const { permissions, token } = useToken();
  const localPermission = permissions;
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [tableData, setTableData] = useState([]);
  const { resources: categories } = useResources({ admin: disableLocking });
  const [error, setError] = useState("");
  const [showValueModal, setShowValueModal] = useState(false);
  const [createdAttribute, setCreatedAttribute] = useState(null);
  const [showConfirmValueModal, setShowConfirmValueModal] = useState(false);
  const [categoryItems, setCategoryItems] = useState([]);
  const [featureAttributesMap, setFeatureAttributesMap] = useState({});
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showModalInfo, setShowModalInfo] = useState(false);
  const [infoItem, setInfoItem] = useState({});
  const [pageLoading, setPageLoading] = useState(true);

  const handleShow = () => {
    setIsEdit(false);
    setEditingAttribute(null);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setIsEdit(false);
    setEditingAttribute({});
    setError("");
  };

  const handleOpenEditModal = (attr) => {
    setIsEdit(true);
    setEditingAttribute(attr);
    setShow(true);
  };

  const fetchAttributes = async () => {
    try {
      const response = await fetchData("/attributes");

      let data = response.data;

      if (disableLocking === false) {
        data = data.filter((attr) => !attr.slug?.startsWith("adm"));
      }

      if (disableLocking === false) {
        data = data.filter((attr) => {
          const hasFeature = featureAttributesMap.hasOwnProperty(attr.id);
          if (!hasFeature) return true;
          return featureAttributesMap[attr.id] === true;
        });
      }

      const sorted = data.sort((a, b) => a.name?.localeCompare(b.name));

      setTableData(sorted);
    } catch (err) {
      console.error("Erro ao buscar atributos:", err);
    }
  };

  const fetchFeatureAttributes = async () => {
    try {
      const response = await fetchData("/feature-attributes");

      const map = {};

      response.data.forEach((item) => {
        const attributeId = item.attribute.id;
        const isFeatureActive = item.feature?.is_enabled === true;

        if (!map[attributeId]) {
          map[attributeId] = false;
        }

        if (isFeatureActive) {
          map[attributeId] = true;
        }
      });

      setFeatureAttributesMap(map);
    } catch (err) {
      console.error("Erro ao buscar feature-attributes:", err);
    }
  };

  const fetchResourcesItems = async (slug) => {
    try {
      const response = await fetchData(`/${slug}`);
      setCategoryItems(response.data);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
    }
  };

  useEffect(() => {
    if (!token) return;

    fetchFeatureAttributes();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    // evita rodar com map vazio no primeiro ciclo
    if (!Object.keys(featureAttributesMap).length) return;

    const loadInitialData = async () => {
      try {
        setPageLoading(true);
        await Promise.all([fetchAttributes()]);
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    };

    loadInitialData();
  }, [featureAttributesMap, disableLocking]);

  const handleDataAdded = (item) => {
    fetchAttributes();
    setCreatedAttribute(item);

    const categorySlug = categories.find(
      (cat) => cat.id === item.resource_id,
    )?.slug;

    if (categorySlug) {
      fetchResourcesItems(categorySlug);
    }

    if (item.is_required) {
      setShowValueModal(true);
      return;
    }

    setShowConfirmValueModal(true);
  };

  const handleDataDeleted = (deletedId) => {
    setTableData((prev) => prev.filter((item) => item.id !== deletedId));
  };

  const handleDataEdited = () => {
    fetchAttributes();
  };

  useEffect(() => {
    if (!createdAttribute || categories.length === 0) return;

    const categorySlug = categories.find(
      (cat) => cat.id === createdAttribute.resource_id,
    )?.slug;

    if (categorySlug) {
      fetchResourcesItems(categorySlug);
    }
  }, [createdAttribute, categories]);

  const handleValueModalHide = () => {
    setShowValueModal(false);
    setCreatedAttribute(null);
    setCategoryItems([]);
  };

  const handleAttributeValuesAdded = () => {
    handleClose();
    fetchAttributes();
  };

  const openModalInfo = (item) => {
    setShowModalInfo(true);
    setInfoItem(item);
  };

  const hideModalInfo = () => {
    setShowModalInfo(false);
    setInfoItem(null);
  };
  const navigationTabs = [
    {
      name: t("Atributos"),
      path: "/atributos",
      iconName: "Atributo",
    },
  ];

  if (pageLoading) {
    return (
      <>
        {!disableLocking && <NavigationTabs tabs={navigationTabs} />}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "60vh" }}
        >
          <div className="text-center">
            <Spinner animation="border" role="status" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!disableLocking && <NavigationTabs tabs={navigationTabs} />}
      <br />
      {tableData.length > 0 ? (
        <AtributosTable
          data={tableData}
          onDataAdded={handleDataAdded}
          onDataDeleted={handleDataDeleted}
          onShowModal={handleShow}
          hideButton={parseInt(localPermission?.register?.attributes) === 11}
          deleteData={deleteData}
          updateData={updateData}
          fetchData={fetchData}
          onEditAttribute={handleOpenEditModal}
          disableLocking={disableLocking}
          onShowResourceModal={() => setShowResourceModal(true)}
          openModalInfo={openModalInfo}
          lockedAttributeIds={featureAttributesMap}
        />
      ) : (
        <NoDataTables
          message={t("Nenhum atributo cadastrado")}
          addItem={handleShow}
        />
      )}

      <AtributosModal
        show={show}
        onHide={handleClose}
        categories={categories}
        onDataAdded={handleDataAdded}
        onDataEdited={handleDataEdited}
        error={error}
        setError={setError}
        postData={postData}
        isEdit={isEdit}
        editingAttribute={editingAttribute}
        fetchData={fetchData}
        updateData={updateData}
        deleteData={deleteData}
      />
      {showValueModal && (
        <AttributesValueModal
          show={showValueModal}
          onHide={handleValueModalHide}
          attribute={createdAttribute}
          categoryItems={categoryItems}
          postData={postData}
          error={error}
          setError={setError}
          onDataAdded={handleAttributeValuesAdded}
        />
      )}
      {showResourceModal && (
        <ModalResourceAttributeValues
          show={showResourceModal}
          onHide={() => setShowResourceModal(false)}
          error={error}
          setError={setError}
          onDataAdded={() => {}}
          disableLocking={disableLocking}
        />
      )}
      {showModalInfo && (
        <ItemAttributesModal
          show={showModalInfo}
          onHide={hideModalInfo}
          item={infoItem}
          isAttribute={true}
          hideButton={parseInt(localPermission?.register?.attributes) === 11}
          disableLocking={disableLocking}
        />
      )}
      {showConfirmValueModal && (
        <Modal
          show={showConfirmValueModal}
          onHide={() => setShowConfirmValueModal(false)}
          centered
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>{t("success_attribute")}</Modal.Title>
          </Modal.Header>

          <Modal.Body>{t("confirm_add_values")}</Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowConfirmValueModal(false)}
            >
              {t("button_no")}
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                setShowConfirmValueModal(false);
                setShowValueModal(true);
              }}
            >
              {t("button_yes")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default Atributos;
